import React, { useState, useEffect } from 'react';
// Added 'Store' for the logo, and 'AlertTriangle' for search advice
import { ShoppingCart, Search, Filter, X, Star, Minus, Plus, ChevronLeft, ChevronRight, CheckCircle, Store, AlertTriangle } from 'lucide-react'; 

// Product Card Component (No Change)
const ProductCard = ({ product, onProductClick, onAddToCart }) => {
  const discountedPrice = product.price - (product.price * product.discountPercentage / 100);
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer">
      <div onClick={() => onProductClick(product)} className="relative">
        <img 
          src={product.thumbnail} 
          alt={product.title}
          className="w-full h-48 object-cover"
        />
        {product.discountPercentage > 0 && (
          <span className="absolute to  p-2 right-2 bg-red-500 text-white px-2 py-1 rounded-md text-sm font-bold">
            -{Math.round(product.discountPercentage)}%
          </span>
        )}
        {product.stock < 10 && product.stock > 0 && (
          <span className="absolute top-2 left-2 bg-orange-500 text-white px-2 py-1 rounded-md text-xs">
            Only {product.stock} left!
          </span>
        )}
      </div>
      <div className="p-4">
        <p className="text-xs text-gray-500 mb-1">{product.brand || product.category}</p>
        <h3 className="font-semibold text-gray-800 mb-2 truncate">{product.title}</h3>
        <div className="flex items-center mb-2">
          <div className="flex text-yellow-400">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={14} fill={i < Math.floor(product.rating) ? "currentColor" : "none"} />
            ))}
          </div>
          <span className="text-xs text-gray-600 ml-2">({product.rating})</span>
        </div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xl font-bold text-orange-600">${discountedPrice.toFixed(2)}</p>
            {product.discountPercentage > 0 && (
              <p className="text-sm text-gray-400 line-through">${product.price.toFixed(2)}</p>
            )}
          </div>
        </div>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onAddToCart(product);
          }}
          disabled={product.stock === 0}
          className={`w-full py-2 rounded-md font-semibold transition-colors ${
            product.stock === 0 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-orange-500 text-white hover:bg-orange-600'
          }`}
        >
          {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
        </button>
      </div>
    </div>
  );
};

// Product Detail Modal (No Change)
const ProductDetailModal = ({ product, onClose, onAddToCart }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const discountedPrice = product.price - (product.price * product.discountPercentage / 100);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full my-8 relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
        >
          <X size={24} />
        </button>
        
        <div className="grid md:grid-cols-2 gap-8 p-8">
          <div>
            <img 
              src={product.images[selectedImage]} 
              alt={product.title}
              className="w-full h-96 object-cover rounded-lg mb-4"
            />
            <div className="flex gap-2 overflow-x-auto">
              {product.images.map((img, idx) => (
                <img 
                  key={idx}
                  src={img}
                  alt={`${product.title} ${idx + 1}`}
                  onClick={() => setSelectedImage(idx)}
                  className={`w-20 h-20 object-cover rounded cursor-pointer border-2 ${
                    selectedImage === idx ? 'border-orange-500' : 'border-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
          
          <div>
            <p className="text-sm text-gray-500 mb-2">{product.brand || product.category}</p>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">{product.title}</h2>
            
            <div className="flex items-center mb-4">
              <div className="flex text-yellow-400 mr-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} fill={i < Math.floor(product.rating) ? "currentColor" : "none"} />
                ))}
              </div>
              <span className="text-gray-600">({product.rating} rating)</span>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-4 mb-2">
                <p className="text-4xl font-bold text-orange-600">${discountedPrice.toFixed(2)}</p>
                {product.discountPercentage > 0 && (
                  <span className="bg-red-500 text-white px-3 py-1 rounded-md font-bold">
                    -{Math.round(product.discountPercentage)}%
                  </span>
                )}
              </div>
              {product.discountPercentage > 0 && (
                <p className="text-lg text-gray-400 line-through">${product.price.toFixed(2)}</p>
              )}
            </div>

            <div className="mb-6 space-y-2">
              <p className="text-gray-700"><span className="font-semibold">Category:</span> {product.category}</p>
              {product.brand && <p className="text-gray-700"><span className="font-semibold">Brand:</span> {product.brand}</p>}
              <p className="text-gray-700"><span className="font-semibold">Stock:</span> {product.stock} units available</p>
              <p className="text-gray-700"><span className="font-semibold">SKU:</span> {product.sku}</p>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-lg mb-2">Description</h3>
              <p className="text-gray-600">{product.description}</p>
            </div>

            {product.tags && product.tags.length > 0 && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag, idx) => (
                    <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <button 
              onClick={() => {
                onAddToCart(product);
                onClose();
              }}
              disabled={product.stock === 0}
              className={`w-full py-3 rounded-lg font-semibold text-lg transition-colors ${
                product.stock === 0 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-orange-500 text-white hover:bg-orange-600'
              }`}
            >
              {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cart Component (No Change)
const Cart = ({ cart, onUpdateQuantity, onRemoveItem, onClose, onCheckout }) => {
  const total = cart.reduce((sum, item) => {
    const discountedPrice = item.price - (item.price * item.discountPercentage / 100);
    return sum + (discountedPrice * item.quantity);
  }, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end md:items-center justify-end">
      <div className="bg-white w-full md:w-96 h-full md:h-auto md:max-h-screen md:rounded-l-lg flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold">Shopping Cart ({cart.length})</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ShoppingCart size={48} className="mx-auto mb-4 opacity-50" />
              <p>Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map(item => {
                const discountedPrice = item.price - (item.price * item.discountPercentage / 100);
                return (
                  <div key={item.id} className="flex gap-4 border-b pb-4">
                    <img src={item.thumbnail} alt={item.title} className="w-20 h-20 object-cover rounded" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                      <p className="text-orange-600 font-bold">${discountedPrice.toFixed(2)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button 
                          onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                          className="bg-gray-200 p-1 rounded hover:bg-gray-300"
                        >
                          <Minus size={16} />
                        </button>
                        <span className="w-8 text-center">{item.quantity}</span>
                        <button 
                          onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                          disabled={item.quantity >= item.stock}
                          className="bg-gray-200 p-1 rounded hover:bg-gray-300 disabled:opacity-50"
                        >
                          <Plus size={16} />
                        </button>
                        <button 
                          onClick={() => onRemoveItem(item.id)}
                          className="ml-auto text-red-500 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t p-4">
            <div className="flex justify-between mb-4">
              <span className="font-semibold text-lg">Total:</span>
              <span className="font-bold text-2xl text-orange-600">${total.toFixed(2)}</span>
            </div>
            <button 
              onClick={onCheckout}
              className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
            >
              Proceed to Checkout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Checkout Success Modal (No Change to functionality, but it uses the correct new props)
const CheckoutSuccessModal = ({ onClose, orderDetails }) => {
    const totalItems = orderDetails.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-8 max-w-sm w-full text-center shadow-2xl">
                <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Order Placed!</h2>
                
                <p className="text-gray-600 mb-4">
                    Your order of **{totalItems} items** has been successfully placed and is being processed.
                </p>

                <div className="text-left mb-6 max-h-40 overflow-y-auto border-t pt-4">
                    <h3 className="text-sm font-semibold mb-2">Items Ordered:</h3>
                    <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                        {orderDetails.map((item, index) => (
                            <li key={index} className="truncate">
                                **{item.quantity}x** {item.title}
                            </li>
                        ))}
                    </ul>
                </div>
                
                <button 
                    onClick={onClose}
                    className="w-full bg-orange-500 text-white py-2 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                >
                    Continue Shopping
                </button>
            </div>
        </div>
    );
};

// Pagination Component (No Change)
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  return (
    <div className="flex items-center justify-center gap-2">
      <button
        onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
        disabled={currentPage === 1}
        className="p-2 rounded-lg bg-white border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
      >
        <ChevronLeft size={20} />
      </button>
      
      {[...Array(totalPages)].map((_, idx) => {
        const page = idx + 1;
        if (
          page === 1 ||
          page === totalPages ||
          (page >= currentPage - 1 && page <= currentPage + 1)
        ) {
          return (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`px-4 py-2 rounded-lg ${
                currentPage === page
                  ? 'bg-orange-500 text-white'
                  : 'bg-white border hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          );
        } else if (page === currentPage - 2 || page === currentPage + 2) {
          return <span key={page}>...</span>;
        }
        return null;
      })}
      
      <button
        onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
        disabled={currentPage === totalPages}
        className="p-2 rounded-lg bg-white border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
      >
        <ChevronRight size={20} />
      </button>
    </div>
  );
};

// Main App - UPDATED
export default function App() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [lastOrderDetails, setLastOrderDetails] = useState([]); 
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false); 
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [sortBy, setSortBy] = useState('default');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;
  const filteredCount = filteredProducts.length; // Used for search advice

  // Fetch products and categories (No Change)
  useEffect(() => {
    // ... fetch logic remains the same
    Promise.all([
      fetch('https://dummyjson.com/products?limit=100').then(res => res.json()),
      fetch('https://dummyjson.com/products/categories').then(res => res.json())
    ]).then(([productsData, categoriesData]) => {
      setProducts(productsData.products);
      setFilteredProducts(productsData.products);
      setCategories(categoriesData);
    });
  }, []);

  // Apply filters (No Change)
  useEffect(() => {
    let result = [...products];

    if (searchQuery) {
      result = result.filter(p => 
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.brand?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      result = result.filter(p => p.category === selectedCategory);
    }

    result = result.filter(p => {
      const price = p.price - (p.price * p.discountPercentage / 100);
      return price >= priceRange[0] && price <= priceRange[1];
    });

    if (sortBy === 'price-low') {
      result.sort((a, b) => {
        const priceA = a.price - (a.price * a.discountPercentage / 100);
        const priceB = b.price - (b.price * b.discountPercentage / 100);
        return priceA - priceB;
      });
    } else if (sortBy === 'price-high') {
      result.sort((a, b) => {
        const priceA = a.price - (a.price * a.discountPercentage / 100);
        const priceB = b.price - (b.price * b.discountPercentage / 100);
        return priceB - priceA;
      });
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'discount') {
      result.sort((a, b) => b.discountPercentage - a.discountPercentage);
    }

    setFilteredProducts(result);
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, priceRange, sortBy, products]);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // ... cart functions remain the same

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: Math.min(item.quantity + 1, product.stock) }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const updateQuantity = (id, quantity) => {
    if (quantity <= 0) {
      setCart(cart.filter(item => item.id !== id));
    } else {
      setCart(cart.map(item => 
        item.id === id ? { ...item, quantity } : item
      ));
    }
  };

  const removeFromCart = (id) => {
    setCart(cart.filter(item => item.id !== id));
  };
  
  const handleCheckout = () => {
    if (cart.length === 0) return;

    const orderDetails = cart.map(item => ({
        id: item.id,
        title: item.title,
        quantity: item.quantity,
    }));

    setLastOrderDetails(orderDetails);
    setCart([]); 
    setShowCart(false); 
    setShowCheckoutSuccess(true); 
  };

  const cartItemsCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Function to generate search advice
  const getSearchAdvice = () => {
      if (searchQuery.length > 2) {
          if (filteredCount === 0) {
              return {
                  type: 'error',
                  message: `No results found for "${searchQuery}". Try using fewer words or resetting your filters.`,
              };
          } else if (filteredCount > 0 && filteredCount < 5 && selectedCategory !== 'all' && priceRange[1] < 2000) {
              return {
                  type: 'warning',
                  message: `Found only ${filteredCount} item(s). Consider relaxing the price range or category filter.`,
              };
          }
      }
      return null;
  };

  const advice = getSearchAdvice();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-orange-500 text-white sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            {/* LOGO ADDITION: Used flex to align icon and text */}
            <div className="flex items-center space-x-2"> 
            <img 
           src="logo.png" // <-- REPLACE THIS PATH with the location of your logo file
           alt="Lazapee Logo"
           className="h-8 w-auto" // Adjust size as needed (e.g., h-8 is 2rem high)
            />
                  <h1 className="text-3xl font-bold">Lazapee</h1>
                </div>
            
            <button 
              onClick={() => setShowCart(true)}
              className="relative bg-white text-orange-500 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2"
            >
              <ShoppingCart size={20} />
              Cart
              {cartItemsCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">
                  {cartItemsCount}
                </span>
              )}
            </button>
          </div>
          
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search products, brands, or categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
            </div>
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className="bg-white text-orange-500 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors md:hidden flex items-center gap-2"
            >
              <Filter size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Filters Sidebar (No Change) */}
          <aside className={`${showFilters ? 'block' : 'hidden'} md:block w-full md:w-64 bg-white p-4 rounded-lg shadow-md h-fit sticky top-24`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-lg">Filters</h2>
              <button 
                onClick={() => setShowFilters(false)}
                className="md:hidden text-gray-500"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-6">
              <label className="block font-semibold mb-2">Sort By</label>
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="default">Default</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="discount">Best Discount</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block font-semibold mb-2">Category</label>
              <select 
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block font-semibold mb-2">
                Price Range: ${priceRange[0]} - ${priceRange[1]}
              </label>
              <input 
                type="range"
                min="0"
                max="2000"
                value={priceRange[1]}
                onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                className="w-full"
              />
            </div>

            <button 
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setPriceRange([0, 2000]);
                setSortBy('default');
              }}
              className="w-full bg-gray-200 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              Reset Filters
            </button>
          </aside>

          {/* Products Grid */}
          <main className="flex-1">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-gray-600">
                Showing {indexOfFirstProduct + 1}-{Math.min(indexOfLastProduct, filteredProducts.length)} of {filteredProducts.length} products
              </p>
            </div>
            
            {/* SEARCH ADVICE FEATURE */}
            {advice && (
                <div className={`flex items-center p-3 rounded-lg mb-4 text-sm ${
                    advice.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                    <AlertTriangle size={20} className="mr-3 flex-shrink-0" />
                    <span>{advice.message}</span>
                </div>
            )}
            {/* END SEARCH ADVICE */}

            {currentProducts.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg">No products found matching your criteria</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                  {currentProducts.map(product => (
                    <ProductCard 
                      key={product.id}
                      product={product}
                      onProductClick={setSelectedProduct}
                      onAddToCart={addToCart}
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <Pagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                )}
              </>
            )}
          </main>
        </div>
      </div>

      {selectedProduct && (
        <ProductDetailModal 
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={addToCart}
        />
      )}

      {showCart && (
        <Cart 
          cart={cart}
          onUpdateQuantity={updateQuantity}
          onRemoveItem={removeFromCart}
          onClose={() => setShowCart(false)}
          onCheckout={handleCheckout} 
        />
      )}
      
      {showCheckoutSuccess && (
        <CheckoutSuccessModal 
          onClose={() => setShowCheckoutSuccess(false)}
          orderDetails={lastOrderDetails}
        />
      )}
    </div>
  );
}